import { Box, Grid2, TextField } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import useNavigationGuard from "../../../hooks/useNavigationGuard";
import CustomSwitch from "../../../components/CustomSwitch/CustomSwitch";
import {
  checkCanUpdate,
  notifyError,
  notifySuccess,
} from "../../../utilities/helper";
import {
  addDepotTypeManagementService,
  fetchDataforListingOptions,
  fetchDepotByIdService,
  updateDepotTypeManagementService,
} from "../../../services/depotManagementService";
import { MenuContext } from "../../../App";
import { CreateHandleUnsaveNavigation } from "../../../utilities/handleUnsaveNavigation";
import SearchableSelect from "../../../components/SearchableSelect";

const AddUpdateDepotManagement = () => {
  const id = useLocation()?.state?.id;

  const [load, setLoad] = useState(false);
  const [countryList, setCountryList] = useState([]);
  const [timeZoneList, setTimeZoneList] = useState([]);
  const [depotTypeList, setDepotTypeList] = useState([]);
  const [canUpdateDepot, setCanUpdateDepot] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const { systemActions } = useContext(MenuContext);
  const handleUnsaveNavigation = CreateHandleUnsaveNavigation();

  const navigate = useNavigate();

  const access_token = localStorage.getItem("access_token");

  const role = localStorage.getItem("role");

  const createDepotSchema = yup.object().shape({
    name: yup
      .string()
      .required("Name is required")
      .matches(
        /^[A-Za-z][A-Za-z0-9\s!@#$%^&*()_+=-]*$/,
        "Name must not start with a number or special character"
      )
      .test("has-letter", "Name must contain at least one alphabet", (value) =>
        /[A-Za-z]/.test(value || "")
      ),
    attention: yup.string().required("Attention is required"),
    address1: yup.string().required("Address is required"),
    address2: yup.string().nullable(),
    address3: yup.string().nullable(),
    city: yup.string().required("City is required"),
    state: yup
      .string()
      .required("State is required")
      .max(8, "State must be at most 8 characters long"),
    zip: yup
      .string()
      .matches(/^\d{5,6}$/, "Zip code must be 5 or 6 digits")
      .required("Zip is required"),
    country: yup.string().required("Country is required"),
    phoneNumber: yup
      .string()
      .matches(/^\d{10}$/, "Phone number must be exactly 10 digits")
      .required("Phone number is required"),
    email: yup
      .string()
      .email("Invalid email format")
      .required("Email Address is required"),
    isActiveEmail: yup.boolean(),
    isActive: yup.boolean(),
    useDst: yup.boolean(),
    timeZone: yup.string().required("Time Zone is required"),
    depotType: yup.string().required("Depot Type is required"),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(createDepotSchema),
    defaultValues: {
      name: "",
      attention: "",
      address1: "",
      address2: "",
      address3: "",
      city: "",
      state: "",
      zip: "",
      country: "",
      phoneNumber: "",
      email: "",
      isActiveEmail: false,
      isActive: false,
      useDst: false,
      timeZone: "",
      depotType: "",
    },
  });

  useNavigationGuard(isDirty);

  useEffect(() => {
    getDataForOptions();
  }, []);

  useEffect(() => {
    if (systemActions?.length > 0 && role !== "Admin" && id) {
      let canUpdate = false;
      const updateFound = systemActions?.find(
        (item) => item?.name === "CanEditDepotList"
      );
      if (updateFound) {
        canUpdate = true;
      }
      setCanUpdateDepot(canUpdate);
    } else {
      if (!id || role === "Admin") {
        setCanUpdateDepot(true);
      }
    }
  }, [systemActions, id]);

  useEffect(() => {
    if (canUpdateDepot) {
      setIsReadOnly(false);
    } else {
      setIsReadOnly(true);
    }
  }, [canUpdateDepot]);

  useEffect(() => {
    if (
      id &&
      countryList &&
      countryList?.length > 0 &&
      timeZoneList &&
      timeZoneList?.length > 0 &&
      depotTypeList &&
      depotTypeList?.length > 0
    ) {
      getDepotDetails(id);
    }
  }, [id, countryList, depotTypeList, timeZoneList]);

  const getDataForOptions = async () => {
    setLoad(true);
    try {
      const res = await fetchDataforListingOptions(access_token);
      if (res?.status == 200) {
        setLoad(false);
        setCountryList(res?.data?.result?.countryList);
        setDepotTypeList(res?.data?.result?.depotTypeList);
        setTimeZoneList(res?.data?.result?.timeZoneList);
      }
    } catch (err) {
      setLoad(false);
      console.log(err.message);
      notifyError(err.message);
    }
  };

  const getDepotDetails = async (id) => {
    setLoad(true);
    try {
      const res = await fetchDepotByIdService(access_token, id);
      if (res?.status == 200) {
        const resp = res?.data?.result;
        setLoad(false);
        const foundTimeZone =
          timeZoneList &&
          timeZoneList?.find((item) => item?.value == resp?.timeZone)?.value;
        const foundCountry =
          countryList &&
          countryList?.find((item) => item?.id === resp.countryId)?.id;
        const foundDepotType =
          depotTypeList &&
          depotTypeList?.find((item) => item?.id == resp?.depotTypeId)?.id;
        reset({
          address1: resp.address1,
          address2: resp.address2,
          address3: resp.address3,
          attention: resp.attention,
          city: resp.city,
          country: foundCountry,
          depotType: foundDepotType,
          email: resp?.emailAddress,
          name: resp?.name,
          phoneNumber: resp.phoneNumber,
          state: resp.state,
          timeZone: foundTimeZone,
          zip: resp.zip,
          isActive: resp.active,
          isActiveEmail: resp.emailActive,
          isDst: resp.useDST,
        });
      }
    } catch (err) {
      setLoad(false);
      console.log(err.message);
      notifyError(err?.message);
      if (err?.status == 401) {
        navigate("/unAuthorize");
      }
    }
  };

  const addUpdateDepot = async (data) => {
    setLoad(true);
    const newData = {
      name: data.name,
      attention: data?.attention,
      address1: data?.address1,
      address2: data?.address2,
      address3: data?.address3,
      city: data?.city,
      state: data?.state,
      zip: data?.zip,
      countryId: data?.country,
      phoneNumber: data?.phoneNumber,
      emailAddress: data?.email,
      timeZone: data?.timeZone,
      emailActive: data?.isActiveEmail,
      active: data?.isActive,
      useDST: data.isDst,
      depotTypeId: data?.depotType,
    };

    try {
      if (!id) {
        const res = await addDepotTypeManagementService(access_token, newData);
        if (res?.data?.status == 409) {
          setLoad(false);
          notifyError("Depot Management already exists !");
          return;
        } else if (res?.status == 200) {
          setLoad(false);
          notifySuccess("Depot Management is created successfully !");
          reset();
          navigate("/depot-management");
        }
      } else {
        const res = await updateDepotTypeManagementService(
          access_token,
          newData,
          id
        );
        if (res?.status == 200) {
          setLoad(false);
          notifySuccess("Depot Management is Updated successfully !");
          reset();
          navigate("/depot-management");
        }
      }
    } catch (err) {
      setLoad(false);
      console.log(err.message);
      notifyError(err.message);
      if (err.status == 401) {
        navigate("/unAuthorize");
      }
    }
  };

  const phoneNumberRegister = register("phoneNumber");
  const zipRegister = register("zip");

  return (
    <>
      {load ? (
        <Loader />
      ) : (
        <div className="content-body">
          <p className="bread-crumbs-link-container">
            <button
              className="bread-crumbs-btn"
              onClick={(e) => handleUnsaveNavigation(e, isDirty, "", navigate)}
            >
              Home
            </button>{" "}
            |{" "}
            <button
              className="bread-crumbs-btn"
              onClick={(e) =>
                handleUnsaveNavigation(e, isDirty, "depot-management", navigate)
              }
            >
              Depot Management
            </button>{" "}
            |{" "}
            <button
              className="bread-crumbs-btn"
              to="/addUpdate-depot-management"
            >
              {id ? "Edit Depot Management" : "Add Depot Management"}
            </button>
            -{" "}
            <b style={{ color: "gray" }}>
              {localStorage?.getItem("studyName")}
            </b>
          </p>
          <Grid2
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%", margin: "0 auto" }}
          >
            <Grid2 item md={12}>
              <p className="page-heading">Depot Details</p>
            </Grid2>
          </Grid2>

          <Box
            component="form"
            autoComplete="off"
            sx={{ height: "auto", width: "100%" }}
            onSubmit={handleSubmit(addUpdateDepot)}
          >
            <Grid2
              container
              alignItems="center"
              justifyContent={"space-between"}
              sx={{ width: "100%", margin: "0 auto" }}
              gap={1}
            >
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Name
                    <span>*</span>
                  </p>
                  <TextField
                    size="small"
                    disabled={isReadOnly}
                    fullWidth
                    type={"text"}
                    {...register("name")}
                  />
                </div>
                {errors.name && (
                  <div className="error-container">{errors.name.message}</div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Attention
                    <span>*</span>
                  </p>
                  <TextField
                    disabled={isReadOnly}
                    fullWidth
                    size="small"
                    type={"text"}
                    {...register("attention")}
                  />
                </div>
                {errors.attention && (
                  <div className="error-container">
                    {errors.attention.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Address 1<span>*</span>
                  </p>
                  <TextField
                    fullWidth
                    size="small"
                    type={"text"}
                    disabled={isReadOnly}
                    {...register("address1")}
                  />
                </div>
                {errors.address1 && (
                  <div className="error-container">
                    {errors.address1.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">Address 2</p>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={isReadOnly}
                    type={"text"}
                    {...register("address2")}
                  />
                </div>
                {errors.address2 && (
                  <div className="error-container">
                    {errors.address2.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">Address 3</p>
                  <TextField
                    fullWidth
                    size="small"
                    type={"text"}
                    disabled={isReadOnly}
                    {...register("address3")}
                  />
                </div>
                {errors.address3 && (
                  <div className="error-container">
                    {errors.address3.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    City
                    <span>*</span>
                  </p>

                  <TextField
                    fullWidth
                    size="small"
                    type={"text"}
                    disabled={isReadOnly}
                    {...register("city")}
                  />
                </div>
                {errors.city && (
                  <div className="error-container">{errors.city.message}</div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    State
                    <span>*</span>
                  </p>

                  <TextField
                    fullWidth
                    size="small"
                    type={"text"}
                    disabled={isReadOnly}
                    {...register("state")}
                  />
                </div>
                {errors.state && (
                  <div className="error-container">{errors.state.message}</div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Zip
                    <span>*</span>
                  </p>
                  <TextField
                    fullWidth
                    disabled={isReadOnly}
                    size="small"
                    type="text"
                    {...zipRegister}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, "");
                      if (value.length > 6) value = value.slice(0, 6);
                      e.target.value = value;
                      zipRegister.onChange(e);
                    }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                </div>
                {errors.zip && (
                  <div className="error-container">{errors.zip.message}</div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Country
                    <span>*</span>
                  </p>
                  <SearchableSelect
                    control={control}
                    data={countryList}
                    name={"country"}
                    optionKey="id"
                    optionLabel="name"
                    isReadOnly={isReadOnly}
                  />
                </div>
                {errors.country && (
                  <div className="error-container">
                    {errors.country.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Phone Number
                    <span>*</span>
                  </p>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={isReadOnly}
                    type="text"
                    {...phoneNumberRegister}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, "");
                      if (value.length > 10) value = value.slice(0, 10);
                      e.target.value = value;
                      phoneNumberRegister.onChange(e);
                    }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                </div>
                {errors.phoneNumber && (
                  <div className="error-container">
                    {errors.phoneNumber.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Email Address
                    <span>*</span>
                  </p>

                  <TextField
                    fullWidth
                    size="small"
                    disabled={isReadOnly}
                    type={"text"}
                    {...register("email")}
                  />
                </div>
                {errors.email && (
                  <div className="error-container">{errors.email.message}</div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Time Zone
                    <span>*</span>
                  </p>
                  <SearchableSelect
                    control={control}
                    data={timeZoneList}
                    name={"timeZone"}
                    optionKey="value"
                    optionLabel="key"
                    isReadOnly={isReadOnly}
                  />
                </div>
                {errors.timeZone && (
                  <div className="error-container">
                    {errors.timeZone.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Depot Type
                    <span>*</span>
                  </p>
                  <SearchableSelect
                    control={control}
                    data={depotTypeList}
                    name={"depotType"}
                    optionKey="id"
                    optionLabel="name"
                    isReadOnly={isReadOnly}
                  />
                </div>
                {errors.depotType && (
                  <div className="error-container">
                    {errors.depotType.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">Email Active</p>
                  <Controller
                    name="isActiveEmail"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <CustomSwitch
                        isSwitch={field.value}
                        disabled={isReadOnly}
                        setSwitch={(val) => field.onChange(val)}
                      />
                    )}
                  />
                </div>
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">Active</p>
                  <Controller
                    name="isActive"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <CustomSwitch
                        disabled={isReadOnly}
                        isSwitch={field.value}
                        setSwitch={(val) => field.onChange(val)}
                      />
                    )}
                  />
                </div>
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">Use DST</p>
                  <Controller
                    name="useDst"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <CustomSwitch
                        isSwitch={field.value}
                        disabled={isReadOnly}
                        setSwitch={(val) => field.onChange(val)}
                      />
                    )}
                  />
                </div>
              </Grid2>
            </Grid2>

            <Grid2
              alignItems={"end"}
              justifyContent="end"
              container
              gap={3}
              sx={{ marginTop: "2%" }}
            >
              <Grid2 item md={6}>
                <button
                  type="button"
                  className="create-btn-outlined"
                  onClick={(e) => {
                    e.preventDefault();
                    handleUnsaveNavigation(
                      e,
                      isDirty,
                      "depot-management",
                      navigate
                    );
                  }}
                >
                  Cancel
                </button>
              </Grid2>
              <Grid2 item md={6}>
                {checkCanUpdate(id, isReadOnly, role) && (
                  <button
                    type="submit"
                    disabled={isReadOnly}
                    className={isReadOnly ? "disabled-btn" : "create-btn"}
                  >
                    {id ? "Update" : "Save"}
                  </button>
                )}
              </Grid2>
            </Grid2>
          </Box>
        </div>
      )}
    </>
  );
};

export default AddUpdateDepotManagement;
